import forEach from 'lodash/forEach';
import AbstractProvider from './AbstractProvider';
import {
  VIEWED_THRESHOLD,
  NOTIFICATION_INTERVAL,
} from 'legacy-src/services/video/constants.js';
import { mergeProgress } from 'chameleon/legacy/src/services/video/utils';

class WistiaProvider extends AbstractProvider {
  constructor(videoId, { progressVector, isImpersonating }, options) {
    super(videoId);
    this.videoId = videoId;
    this.progressVector = progressVector;
    //https://wistia.com/support/developers/embed-options#setting-bounds-for-videofoam
    this._options = options || {
      progressTrackingKey: '',
      time: 0,
    };

    this.previousTime = null;
    window.wistiaInitQueue = window.wistiaInitQueue || [];
    // STOP THE TRACK when teacher is impersonating student
    if (isImpersonating) {
      window.wistiaInitQueue.push((video) => video.consent(false));
    }
    window.wistiaInitQueue.push(this._initCallback.bind(this));
    this.embedInitConfig = {
      id: videoId,
      onHasData: (video) => {
        /**
         * Don't manually set the time when the user has watched none
         * of the video since the player will put them in the right place
         * by default
         */

        /**
         * we mute and unmute because to seek a particular time, wistia
         * actually plays and pauses the video (very quickly) leading to a
         * split second where theres a sound from the video
         */
        const pauseUnPause = (vid) => {
          vid.mute();
          vid.bind('pause', () => {
            vid.unmute();
            return vid.unbind;
          });
        };

        if (this._options.time > 0 && this._options.time < 1) {
          pauseUnPause(video);
          video.time(Math.floor(video.duration() * this._options.time));
        } else if (this._options.time >= 1) {
          pauseUnPause(video);
          video.time(0);
        }
      },
      onReady: (video) => {
        /**
         * if this video is being opened for the very first time
         * create an empty progress vector
         */
        const duration = video.duration();
        if (this.progressVector.length === 0) {
          this.progressVector = Array.from({ length: duration + 1 }, () => 0);
        } else if (this.progressVector.length < duration + 1) {
          /**
           * if we already have a vector but it's length is less than the video duration,
           * fill in the missing seconds
           */

          this.progressVector = mergeProgress(
            this.progressVector,
            Array.from({ length: duration + 1 }, () => 0),
          );
        }

        this.previousTime = parseInt((this._options.time || 0) * duration, 10);

        video.bind('timechange', (t) => {
          this.updateProgressVector(t);
        });
        /**
         * update the last second of the vector with a seperate `end` event
         */
        video.bind('end', () => {
          const lastSecond = parseInt(duration, 10);
          this.progressVector[lastSecond] += 1;
        });
      },
    };
    window.wistiaInitQueue.push(this.embedInitConfig);

    this._previousSecondsWatched = 0;
    this._bindEventsStack = [];
    this._eventsTranslation = {
      onplay: 'play',
      onend: 'end',
      onpause: 'pause',
    };
    this._customEvents = {
      playing: {
        eventFireCallback: '_onPlayInit',
        eventCallback: '_onPlaying',
        providerEvent: 'secondchange',
      },
    };
    this._customEventsStack = [];
  }

  updateProgressVector = (t) => {
    const prevSecond = parseInt(this.previousTime, 10);
    const newSecond = parseInt(t, 10);
    /**
     * perform no updates on the vector when change in time is too rapid i.e.
     * when the difference of the seconds is greater than 2 seconds (since play-rate is <= 2)
     */
    if (Math.abs(prevSecond - newSecond) > 2) {
      this.previousTime = t;
      return;
    }

    /**
     * timechange has been fired before so we can know if we should update
     * the vector
     */

    /**
     * in this case the video is playing forward
     */
    if (prevSecond < newSecond) {
      this.progressVector[prevSecond] =
        this.progressVector[prevSecond] + 1 || 1;
    }

    /**
     * keep the time cached for future comparisons
     */
    this.previousTime = t;
  };

  _initCallback(wistiaHandler) {
    this.wistiaHandler = wistiaHandler;

    this.wistiaHandler.options(this.videoId, {
      autoPlay: false,
      videoFoam: true,
      seo: false,
      resumable: false,
    });

    this._embedVideo().then(() => {
      this._configProgressTracking();
      this._bindEvents();
      this._fireCustomEvents();
    });
  }

  _bindEvents() {
    this._bindEventsStack.forEach((eventData) => {
      const { event, callback } = eventData;
      this._bind(event, callback);
    });
  }

  _fireCustomEvents() {
    forEach(this._customEventsStack, ({ event, callback }) => {
      const { eventFireCallback, providerEvent } = this._customEvents[event];
      const internalCallback = this[eventFireCallback];
      if (internalCallback) {
        internalCallback.call(this, providerEvent, callback);
      }
    });
  }

  _configProgressTracking() {
    if (this.videoHandler && this._options.progressTrackingKey) {
      this.videoHandler.email(this._options.progressTrackingKey);
    }
  }

  async _embedVideo() {
    await this.wistiaHandler.embeds.setup();
    this.videoHandler = this.wistiaHandler.api(this.videoId);
  }

  _isEventAvailable(rawEvent) {
    const internalEvent = this._eventsTranslation[rawEvent];
    return Boolean(internalEvent);
  }

  _isEventCustom(event) {
    return Boolean(this._customEvents[event]);
  }

  _getEventTranslation(rawEvent) {
    return this._eventsTranslation[rawEvent];
  }

  _getStack(isCustom) {
    return this[isCustom ? '_customEventsStack' : '_bindEventsStack'];
  }

  /**
   * Generates private callback
   * @param {string} func
   * @param {function} callback
   * */
  _createCallbackRef(func, callback) {
    this[`${func}Callback`] = callback;

    return this[`${func}Callback`];
  }

  _getCallbackRef(func) {
    return this[`${func}Callback`];
  }

  _deleteCallbackRef(func) {
    delete this[`${func}Callback`];
  }

  _onPlayInit(providerEvent, eventCallback) {
    const _callback = this._createCallbackRef(
      '_onPlaying',
      this._onPlaying.bind(this, eventCallback),
    );
    this._bind(providerEvent, _callback);
  }

  _onPlaying(callback) {
    const thresholdReached = this.isThresholdReached();
    const newSecondsHaveElapsed = this.newSecondsHaveElapsed();
    callback.call(null, thresholdReached, newSecondsHaveElapsed);
  }

  /**
   * Width,Height functionality must be made private
   * */
  width(value) {
    if (this.videoHandler) {
      this.videoHandler.videoWidth(value, { constrain: true });
    }
  }

  height(value) {
    if (this.videoHandler) {
      this.videoHandler.videoHeight(value, { constrain: true });
    }
  }

  _bindEvent(internalEvent, callback, isCustom) {
    const stack = this._getStack(isCustom);

    stack.push({
      event: internalEvent,
      callback,
    });
  }

  _bind(event, callback) {
    if (this.videoHandler) {
      this.videoHandler.bind(event, callback);
    }
  }

  /**
   * Prepares the events stack for binding when video is available for binding
   * */
  bind(event, callback) {
    if (this._isEventCustom(event)) {
      this._bindEvent(event, callback, true);
      return;
    }
    if (this._isEventAvailable(event)) {
      this._bindEvent(this._getEventTranslation(event), callback, false);
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        event,
        'This event does not exist in:',
        this._eventsTranslation,
        'or',
        this._customEventsStack,
      );
    }
  }

  _unbind(event, callback) {
    if (this.videoHandler) {
      this.videoHandler.unbind(event, callback);
    }
  }

  _unbindEvent(internalEvent, callback) {
    const stack = this._bindEventsStack;
    const foundIndex = stack.findIndex(
      ({ event: _event, callback: _callback }) =>
        _event === internalEvent && _callback === callback,
    );
    if (foundIndex > -1) {
      this._unbind(internalEvent, callback);
      stack.splice(foundIndex, 1);
    }
  }

  _unbindCustomEvent(
    internalEvent,
    providerEvent,
    callback,
    generatedCallback,
  ) {
    const stack = this._customEventsStack;
    const foundIndex = stack.findIndex(
      ({ event: _event, callback: _callback }) =>
        _event === internalEvent && _callback === callback,
    );
    if (foundIndex > -1) {
      this._unbind(providerEvent, generatedCallback);
      stack.splice(foundIndex, 1);
      const { eventCallback } = this._customEvents[internalEvent];
      this._deleteCallbackRef(eventCallback);
    }
  }

  unbind(event, callback) {
    if (this._isEventCustom(event)) {
      const { providerEvent, eventCallback } = this._customEvents[event];
      const generatedCallback = this._getCallbackRef(eventCallback);
      this._unbindCustomEvent(
        event,
        providerEvent,
        callback,
        generatedCallback,
      );
      return;
    }

    if (this._isEventAvailable(event)) {
      const internalEvent = this._getEventTranslation(event);
      this._unbindEvent(internalEvent, callback);
    }
  }

  isThresholdReached() {
    return this._getPercentWatched() >= VIEWED_THRESHOLD;
  }

  newSecondsHaveElapsed() {
    const secondsWatched = this._getSecondsWatched();
    if (
      this._previousSecondsWatched <= secondsWatched &&
      secondsWatched % NOTIFICATION_INTERVAL === 0
    ) {
      this._previousSecondsWatched = secondsWatched;

      return true;
    }

    return false;
  }

  latestVideoPosition() {
    const duration = this._getDuration();
    const time = this._getCurrentVideoTime();

    if (!duration || !time) {
      return 0.0;
    }

    const percent = time / duration;
    return percent.toFixed(4);
  }

  /**
   * get the second watched vector array
   * @returns Array([1,1,3,3.....])
   * @link FineTune devs did this!
   */
  getProgressVector() {
    return this.progressVector;
  }

  _getSecondsWatched() {
    if (this.videoHandler) {
      return this.videoHandler.secondsWatched();
    }
    return 0.0;
  }

  _getPercentWatched() {
    if (this.videoHandler) {
      return this.videoHandler.percentWatched();
    }
    return 0.0;
  }

  getInternalData() {
    const wp = this._getPercentWatched();
    const watchedPercentage = wp ? wp.toFixed(2) : '0.00';
    const latestVideoPosition = this.latestVideoPosition();
    return {
      progress: this.getProgressVector(),
      watchedPercentage,
      latestVideoPosition,
    };
  }

  _getDuration() {
    if (this.videoHandler && this.videoHandler.hasData()) {
      return this.videoHandler.duration();
    }
  }

  _getCurrentVideoTime() {
    if (this.videoHandler) {
      return this.videoHandler.time();
    }
    return 0.0;
  }

  destroy() {
    if (this.videoHandler) {
      this.videoHandler.remove();
      window.wistiaInitQueue.push({ revoke: this.embedInitConfig });
      this.videoHandler = null;
    }
  }
}

export default WistiaProvider;
